import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  X,
  Crown,
  Loader2,
  Sparkles,
  Building2,
  Users,
  Rocket,
  Shield,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { billingApi, getErrorMessage } from "@/services/api";
import { generateUUID } from "@/utils/transform";

export const Plans = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    plan: null,
  });
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // ============================================
  // TanStack Query - Fetch Plans and Subscription
  // ============================================
  const { data: plansData, isLoading: loading } = useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => {
      const [plansRes, subRes] = await Promise.all([
        billingApi.getPlans(),
        billingApi.getSubscription(),
      ]);
      return {
        plans: plansRes.data.data || [],
        subscription: subRes.data.data,
      };
    },
    meta: {
      onError: () => {
        toast.error(t("toast.failedLoadBilling"));
      },
    },
  });

  const plans = plansData?.plans || [];
  const subscription = plansData?.subscription;

  // ============================================
  // TanStack Query - Plan Change Mutation
  // ============================================
  const changePlanMutation = useMutation({
    mutationFn: ({ planId }) => {
      if (planId === "free") {
        return billingApi.cancelSubscription(false);
      }
      return billingApi.changePlan(planId, billingInterval);
    },
    onSuccess: (_, { planId }) => {
      if (planId === "free") {
        toast.success(t("billing.subscriptionCancelNotice"));
      } else {
        toast.success(t("toast.planChanged"));
      }
      queryClient.invalidateQueries({ queryKey: ["billing-plans"] });
      queryClient.invalidateQueries({ queryKey: ["billing-subscription"] });
      setConfirmDialog({ open: false });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t("errors.failedChangePlan")));
    },
    onSettled: () => {
      setCheckoutLoading(null);
    },
  });

  const handleSelectPlan = async (planId) => {
    const currentPlan = subscription?.plan;

    if (
      subscription?.plan === "custom" ||
      subscription?.billingMode === "invoice"
    ) {
      window.location.href =
        "mailto:sales@escribe.io?subject=Plan change request";
      return;
    }

    if (planId === currentPlan) {
      toast(t("billing.alreadyOnPlan"));
      return;
    }

    if (planId === "enterprise") {
      window.location.href =
        "mailto:sales@escribe.io?subject=Enterprise Plan Inquiry";
      return;
    }

    if (planId === "free" && currentPlan !== "free") {
      setConfirmDialog({ open: true, plan: planId, action: "downgrade" });
      return;
    }

    // Check if upgrading or new subscription
    if (currentPlan === "free" || !subscription?.stripeSubscriptionId) {
      await handleCheckout(planId);
    } else {
      setConfirmDialog({ open: true, plan: planId, action: "change" });
    }
  };

  const handleCheckout = async (planId) => {
    try {
      setCheckoutLoading(planId);
      const response = await billingApi.createCheckoutSession(
        planId,
        billingInterval,
      );
      window.location.href = response.data.data.url;
    } catch (err) {
      toast.error(getErrorMessage(err, t("errors.failedStartCheckout")));
      setCheckoutLoading(null);
    }
  };

  const handleChangePlan = (planId) => {
    setCheckoutLoading(planId);
    changePlanMutation.mutate({ planId, action: confirmDialog.action });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentPlan = subscription?.plan || "free";

  // A negotiated plan is set by sales and billed by manual invoice. The plans
  // stay visible (customers should still see what exists), but every
  // self-serve action is refused, and the backend refuses it too
  // (billing.service.assertSelfServeBillingAllowed) regardless of this UI.
  const isManagedPlan =
    subscription?.plan === "custom" || subscription?.billingMode === "invoice";
  const yearlyDiscount = 33; // 33% discount for yearly billing

  const getVariant = (plan, isCurrentPlan) => {
    if (plan?.popular) {
      return "default";
    }
    return isCurrentPlan ? "outline" : "secondary";
  };

  const getCurrentPlanTitle = (plan, currentPlanId, isCurrentPlan, managed) => {
    if (isCurrentPlan) {
      return t("billing.currentPlan");
    }
    if (managed) {
      return t("billing.contactSales");
    }
    if (plan?.id === "enterprise") {
      return t("billing.contactSales");
    }
    if (plan.id === "free") {
      return t("common.downgrade");
    }
    return currentPlanId === "free"
      ? t("billing.getStarted")
      : t("billing.switchPlan");
  };

  const getAPICallsPerMonth = (plan) => {
    if (plan?.limits?.apiCallsPerMonth === -1) {
      return "∞";
    }
    return plan?.limits?.apiCallsPerMonth === 0
      ? "-"
      : plan?.limits?.apiCallsPerMonth?.toLocaleString();
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case "free":
        return <Users className="h-8 w-8 text-muted-foreground" />;
      case "starter":
        return <Rocket className="h-8 w-8 text-blue-500" />;
      case "professional":
        return <Crown className="h-8 w-8 text-yellow-500" />;
      case "business":
        return <Building2 className="h-8 w-8 text-purple-500" />;
      case "enterprise":
        return <Shield className="h-8 w-8 text-primary" />;
      default:
        return <Users className="h-8 w-8 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("billing.plans.title")}
          </h2>
          <p className="text-muted-foreground">
            Choose the perfect plan for your business needs
          </p>
        </div>
      </div>

      {isManagedPlan && (
        <Alert>
          <Building2 className="h-4 w-4" />
          <AlertTitle>
            {subscription?.planDetails?.name || "Custom"} plan — managed by our
            team
          </AlertTitle>
          <AlertDescription>
            Your plan, pricing and allowances are agreed with us and billed by
            invoice, so it can&apos;t be changed here. The plans below are shown
            for reference — contact sales to make a change.
          </AlertDescription>
        </Alert>
      )}

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <Label
          htmlFor="billing-toggle"
          className={
            billingInterval === "monthly"
              ? "font-semibold"
              : "text-muted-foreground"
          }
        >
          Monthly
        </Label>
        <Switch
          id="billing-toggle"
          checked={billingInterval === "yearly"}
          onCheckedChange={(checked) =>
            setBillingInterval(checked ? "yearly" : "monthly")
          }
        />
        <Label
          htmlFor="billing-toggle"
          className={
            billingInterval === "yearly"
              ? "font-semibold"
              : "text-muted-foreground"
          }
        >
          Yearly
          <Badge variant="secondary" className="ml-2">
            Save {yearlyDiscount}%
          </Badge>
        </Label>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 pt-4">
        {plans.map((plan) => {
          const isCurrentPlan = plan.id === currentPlan;
          const price =
            billingInterval === "yearly"
              ? plan.priceYearlyMonthly
              : plan.priceMonthlyDisplay;
          const originalPrice =
            billingInterval === "yearly" ? plan.priceMonthlyDisplay : null;

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col ${
                plan.popular ? "border-primary shadow-lg scale-105" : ""
              } ${isCurrentPlan ? "ring-2 ring-primary" : ""}`}
            >
              {/* Badges container */}
              <div className="absolute -top-3 left-0 right-0 flex justify-center gap-2">
                {plan.popular && (
                  <Badge className="bg-primary">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Most Popular
                  </Badge>
                )}
                {isCurrentPlan && (
                  <Badge variant="outline" className="bg-background">
                    Current Plan
                  </Badge>
                )}
              </div>

              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-2">{getPlanIcon(plan.id)}</div>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>

              <CardContent className="text-center grow">
                <div className="mb-4">
                  {price !== null ? (
                    <>
                      {originalPrice && (
                        <div className="text-sm text-muted-foreground line-through">
                          ${originalPrice}/mo
                        </div>
                      )}
                      <div className="text-4xl font-bold">${price}</div>
                      <div className="text-sm text-muted-foreground">
                        per user / month
                        {billingInterval === "yearly" && (
                          <span className="block text-xs">billed annually</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-bold">
                        {t("common.custom")}
                      </div>
                      {plan.basePriceIndicator && (
                        <div className="text-sm text-muted-foreground">
                          Starting at ${plan.basePriceIndicator / 100}/mo
                        </div>
                      )}
                    </>
                  )}
                </div>

                <ul className="space-y-2 text-sm text-left">
                  {plan.featureList?.slice(0, 6).map((feature) => (
                    <li key={generateUUID()} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {plan.featureList?.length > 6 && (
                    <li className="text-muted-foreground text-xs">
                      +{plan.featureList.length - 6} more features
                    </li>
                  )}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  className="w-full"
                  variant={getVariant(plan, isCurrentPlan)}
                  disabled={isCurrentPlan || checkoutLoading === plan.id}
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  {checkoutLoading === plan.id && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {getCurrentPlanTitle(
                    plan,
                    currentPlan,
                    isCurrentPlan,
                    isManagedPlan,
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Feature Comparison */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{t("billing.plans.featureComparison")}</CardTitle>
          <CardDescription>
            {t("billing.plans.featureComparisonDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">
                    {t("billing.plans.feature")}
                  </th>
                  {plans.map((plan) => (
                    <th key={plan.id} className="text-center py-3 px-4">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Limits */}
                <FeatureRow
                  feature={t("features.envelopesPerMonth")}
                  values={plans.map((p) =>
                    p.limits?.envelopesPerMonth === -1
                      ? "∞"
                      : p.limits?.envelopesPerMonth,
                  )}
                />
                <FeatureRow
                  feature={t("features.templates")}
                  values={plans.map((p) =>
                    p.limits?.templates === -1 ? "∞" : p.limits?.templates,
                  )}
                />
                <FeatureRow
                  feature={t("features.teamMembers")}
                  values={plans.map((p) =>
                    p.limits?.teamMembers === -1 ? "∞" : p.limits?.teamMembers,
                  )}
                />

                {/* Core signing features */}
                <FeatureRow
                  feature={t("features.bulkSend")}
                  values={plans.map((p) => p.features?.bulkSend)}
                  isBoolean
                />
                <FeatureRow
                  feature={t("features.signingLinks")}
                  values={plans.map((p) => p.features?.signingLinks)}
                  isBoolean
                />
                <FeatureRow
                  feature={t("features.inPersonSigning")}
                  values={plans.map((p) => p.features?.inPersonSigning)}
                  isBoolean
                />
                <FeatureRow
                  feature={t("features.sequentialSigning")}
                  values={plans.map((p) => p.features?.sequentialSigning)}
                  isBoolean
                />

                {/* Collapsible section for advanced features */}
                <tr>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- presentational cell; the toggle Button carries its own text label, nested inside Collapsible beyond the rule's depth */}
                  <td colSpan={plans.length + 1} className="p-0">
                    <Collapsible
                      open={showAllFeatures}
                      onOpenChange={setShowAllFeatures}
                    >
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className="w-full justify-center text-muted-foreground hover:text-foreground"
                        >
                          {showAllFeatures ? (
                            <>
                              <ChevronUp className="h-4 w-4 mr-2" />
                              Show fewer features
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4 mr-2" />
                              Show all features
                            </>
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <table className="w-full text-sm">
                          <tbody>
                            {/* Automation */}
                            <FeatureRow
                              feature="Auto Reminders"
                              values={plans.map(
                                (p) => p.features?.autoReminders,
                              )}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Document Generation"
                              values={plans.map(
                                (p) => p.features?.documentGeneration,
                              )}
                              isBoolean
                            />

                            {/* Branding */}
                            <FeatureRow
                              feature="Custom Branding"
                              values={plans.map(
                                (p) => p.features?.customBranding,
                              )}
                              isBoolean
                            />
                            <FeatureRow
                              feature="White-Label"
                              values={plans.map((p) => p.features?.whiteLabel)}
                              isBoolean
                            />

                            {/* Developer */}
                            <FeatureRow
                              feature="API Access"
                              values={plans.map((p) => p.features?.apiAccess)}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Webhooks"
                              values={plans.map((p) => p.features?.webhooks)}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Embed SDK"
                              values={plans.map((p) => p.features?.embedSdk)}
                              isBoolean
                            />
                            <FeatureRow
                              feature="API Calls/month"
                              values={plans.map((plan) =>
                                getAPICallsPerMonth(plan),
                              )}
                            />

                            {/* Analytics */}
                            <FeatureRow
                              feature={t("features.advancedAnalytics")}
                              values={plans.map(
                                (p) => p.features?.advancedAnalytics,
                              )}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Audit Trail Exports"
                              values={plans.map(
                                (p) => p.features?.auditExports,
                              )}
                              isBoolean
                            />

                            {/* Certification */}
                            <FeatureRow
                              feature="Certificate of Completion"
                              values={plans.map(
                                (p) => p.features?.basicCertification,
                              )}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Timestamp Authority (RFC 3161)"
                              values={plans.map(
                                (p) => p.features?.timestampAuthority,
                              )}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Qualified Signatures (eIDAS)"
                              values={plans.map(
                                (p) => p.features?.qualifiedSignatures,
                              )}
                              isBoolean
                            />

                            {/* Security */}
                            <FeatureRow
                              feature="SSO / SAML"
                              values={plans.map((p) => p.features?.sso)}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Advanced Security"
                              values={plans.map(
                                (p) => p.features?.advancedSecurity,
                              )}
                              isBoolean
                            />

                            {/* Team */}
                            <FeatureRow
                              feature="Role Management"
                              values={plans.map(
                                (p) => p.features?.roleManagement,
                              )}
                              isBoolean
                            />

                            {/* Support */}
                            <FeatureRow
                              feature="Priority Support"
                              values={plans.map(
                                (p) => p.features?.prioritySupport,
                              )}
                              isBoolean
                            />
                            <FeatureRow
                              feature="Dedicated Account Manager"
                              values={plans.map(
                                (p) => p.features?.dedicatedManager,
                              )}
                              isBoolean
                            />
                          </tbody>
                        </table>
                      </CollapsibleContent>
                    </Collapsible>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.action === "downgrade"
                ? t("billing.downgradePlan")
                : t("billing.changePlan")}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.action === "downgrade"
                ? t("billing.downgradeConfirm")
                : t("billing.planChangeProrated")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false })}
              size={isMobile ? "mobile" : "default"}
            >
              Cancel
            </Button>
            <Button
              variant={
                confirmDialog.action === "downgrade" ? "destructive" : "default"
              }
              onClick={() => handleChangePlan(confirmDialog.plan)}
              disabled={checkoutLoading}
              size={isMobile ? "mobile" : "default"}
            >
              {checkoutLoading && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {confirmDialog.action === "downgrade"
                ? t("billing.confirmDowngrade")
                : t("billing.confirmChange")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAQ */}
      <div className="text-center mt-8 space-y-2">
        <p className="text-sm text-muted-foreground">
          Need help choosing?{" "}
          <a
            href="mailto:sales@escribe.io"
            className="text-primary hover:underline"
          >
            Contact our sales team
          </a>
        </p>
        <p className="text-xs text-muted-foreground">
          All plans include SSL encryption, certificate of completion, audit
          trails, and email support.
        </p>
      </div>
    </div>
  );
};

const FeatureRow = ({ feature, values, isBoolean = false }) => {
  const renderFeatureRow = (value) => {
    if (isBoolean) {
      return value ? (
        <Check className="h-4 w-4 text-green-500 mx-auto" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground mx-auto" />
      );
    }
    return (
      <span className={value === "∞" ? "text-primary font-semibold" : ""}>
        {value}
      </span>
    );
  };

  return (
    <tr className="border-b">
      <td className="py-3 px-4 font-medium">{feature}</td>
      {values.map((value) => (
        <td key={generateUUID()} className="text-center py-3 px-4">
          {renderFeatureRow(value)}
        </td>
      ))}
    </tr>
  );
};

export default Plans;
